import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  RefreshCw, Users, CheckCircle, Clock, UserPlus, UserX,
  Activity, ArrowUpDown, ChevronUp, ChevronDown,
  AlertTriangle, Phone, Briefcase, Database,
} from "lucide-react";
import DateFilter from "../components/DateFilter";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { PaginationControls } from "../components/Pagination";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { relativeTime, RingbaWarning } from "../components/SyncDataButton";
import { CopyButton } from "../components/CopyField";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CFG = {
  Active:        { bg: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-500/20", dot: "bg-emerald-400", icon: CheckCircle, rowBg: "" },
  New:           { bg: "bg-blue-500/10",    text: "text-blue-400",    border: "border-blue-500/20",    dot: "bg-blue-400",    icon: UserPlus,    rowBg: "" },
  "Low Activity":{ bg: "bg-amber-500/10",   text: "text-amber-400",  border: "border-amber-500/20",   dot: "bg-amber-400",   icon: AlertTriangle, rowBg: "bg-amber-500/[0.03]" },
  Inactive:      { bg: "bg-red-500/10",     text: "text-red-400",    border: "border-red-500/20",     dot: "bg-red-400",     icon: UserX,       rowBg: "bg-red-500/[0.04]" },
};

const QUICK_FILTERS = [
  { key: "all",          label: "All",          color: "text-slate-300 bg-slate-800/60 border-slate-700/50" },
  { key: "Active",       label: "Active",       color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/25" },
  { key: "New",          label: "New",          color: "text-blue-400 bg-blue-500/10 border-blue-500/25" },
  { key: "Low Activity", label: "Low Activity", color: "text-amber-400 bg-amber-500/10 border-amber-500/25" },
  { key: "Inactive",     label: "Inactive",     color: "text-red-400 bg-red-500/10 border-red-500/25" },
];

export default function PublisherActivityPage() {
  const [publishers, setPublishers] = useState([]);
  const [breakdown, setBreakdown] = useState({});
  const [total, setTotal] = useState(0);
  const [lastSynced, setLastSynced] = useState(null);
  const [ringbaData, setRingbaData] = useState({});
  const [ringbaUnavailable, setRingbaUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [quickFilter, setQuickFilter] = useState("all");
  const [sortBy, setSortBy] = useState("calls_30d");
  const [sortOrder, setSortOrder] = useState("desc");
  const [dateFilter, setDateFilter] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 50;
  const intervalRef = useRef(null);

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const params = new URLSearchParams();

      // Always scope to the selected range — backend computes live from call analytics
      if (dateFilter?.startDate && dateFilter?.endDate) {
        params.set("start_date", dateFilter.startDate);
        params.set("end_date", dateFilter.endDate);
      }

      const [summaryRes, ringbaRes] = await Promise.all([
        axios.get(`${API}/publisher-activity/summary?${params}`, { headers }),
        axios.get(`${API}/publishers/ringba-assignments`, { headers }).catch(() => ({ data: { publishers: {} } })),
      ]);

      const data = summaryRes.data;
      setPublishers(data.publishers || []);
      setTotal(data.total || 0);
      setBreakdown(data.status_breakdown || {});
      setLastSynced(data.last_synced || new Date().toISOString());
      setRingbaUnavailable(!!data.ringba_unavailable);
      setRingbaData(ringbaRes.data?.publishers || {});
    } catch (err) {
      console.error("Failed to fetch publisher activity:", err);
      if (!silent) toast.error("Failed to load publisher activity data");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [dateFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 5 minutes (data is cached, not expensive)
  useEffect(() => {
    intervalRef.current = setInterval(() => fetchData(true), 300000);
    return () => clearInterval(intervalRef.current);
  }, [fetchData]);

  // Client-side sort
  const sorted = useMemo(() => {
    const arr = [...publishers];
    arr.sort((a, b) => {
      let va = a[sortBy], vb = b[sortBy];
      if (typeof va === "string") va = va.toLowerCase();
      if (typeof vb === "string") vb = vb.toLowerCase();
      if (va == null) va = sortOrder === "asc" ? Infinity : -Infinity;
      if (vb == null) vb = sortOrder === "asc" ? Infinity : -Infinity;
      if (va < vb) return sortOrder === "asc" ? -1 : 1;
      if (va > vb) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [publishers, sortBy, sortOrder]);

  // Client-side quick filter
  const filtered = useMemo(
    () => quickFilter === "all" ? sorted : sorted.filter((p) => p.status === quickFilter),
    [sorted, quickFilter]
  );

  useEffect(() => { setCurrentPage(1); }, [quickFilter, dateFilter]);

  const handleSort = (field) => {
    if (sortBy === field) setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    else { setSortBy(field); setSortOrder("desc"); }
  };

  const handleManualSync = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/publisher-activity/sync`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Publisher activity synced");
      await fetchData();
    } catch (err) {
      console.error("Sync failed:", err);
      toast.error("Sync failed. Check Ringba configuration.");
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Not available";
    try { return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
    catch { return dateStr; }
  };

  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((t) => t + 1), 15000);
    return () => clearInterval(id);
  }, []);
  const formatSyncTime = (dateStr) => relativeTime(dateStr);

  const statusCount = (key) => key === "all" ? total : (breakdown[key] || 0);

  const activityReason = (pub) => {
    if (pub.status === "Inactive") return pub.status_source === "ringba" ? "Disabled on Ringba" : "No calls in period";
    if (pub.status === "New") return "First call within last 30 days";
    if (pub.status === "Low Activity") return `Low activity (${pub.calls_30d} call${pub.calls_30d !== 1 ? "s" : ""})`;
    return `${(pub.calls_30d || 0).toLocaleString()} call${pub.calls_30d !== 1 ? "s" : ""} in period`;
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown className="h-3 w-3 ml-1 text-slate-600" />;
    return sortOrder === "asc"
      ? <ChevronUp className="h-3 w-3 ml-1 text-blue-400" />
      : <ChevronDown className="h-3 w-3 ml-1 text-blue-400" />;
  };

  const statCards = [
    { label: "Total Publishers", value: total, icon: Users, color: "blue" },
    { label: "Active", value: breakdown.Active || 0, icon: CheckCircle, color: "emerald" },
    { label: "New", value: breakdown.New || 0, icon: UserPlus, color: "blue" },
    { label: "Low Activity", value: breakdown["Low Activity"] || 0, icon: AlertTriangle, color: "amber" },
    { label: "Inactive", value: breakdown.Inactive || 0, icon: UserX, color: "red" },
  ];

  return (
    <div className="space-y-6" data-testid="publisher-activity-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-white tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>Publisher Activity</h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Publisher list from Ringba · call stats from call analytics
          </p>
        </div>
        <div className="flex items-center gap-2">
          <RingbaWarning show={ringbaUnavailable} testId="sync-data-btn-ringba-warning" />
          {/* Last Synced indicator */}
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#11141D] border border-white/5 rounded-lg" data-testid="last-synced-indicator">
            <Database className="h-3.5 w-3.5 text-slate-500" />
            <span className="text-[11px] text-slate-400">
              Last Synced: <span className="text-slate-300 font-medium" data-testid="last-synced-time">{formatSyncTime(lastSynced)}</span>
            </span>
          </div>
          <button
            onClick={handleManualSync}
            disabled={syncing}
            className="flex items-center gap-1.5 bg-blue-600/15 border border-blue-500/30 rounded-lg px-3 py-1.5 text-sm text-blue-300 hover:bg-blue-600/25 transition disabled:opacity-60"
            data-testid="sync-data-btn"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", syncing && "animate-spin")} />
            <span className="text-sm">{syncing ? "Syncing..." : "Sync Data"}</span>
          </button>
          <button
            onClick={() => fetchData()}
            disabled={loading}
            className="flex items-center gap-1.5 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg px-3 py-1.5 transition-all"
            data-testid="refresh-activity"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
            <span className="text-sm">Refresh</span>
          </button>
        </div>
      </div>

      {/* Date Filter */}
      <DateFilter onFilterChange={setDateFilter} defaultPreset="last_30_days" />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {statCards.map((card) => {
          const colorMap = { blue: "text-blue-400", emerald: "text-emerald-400", amber: "text-amber-400", red: "text-red-400" };
          const bgMap = { blue: "bg-blue-500/10", emerald: "bg-emerald-500/10", amber: "bg-amber-500/10", red: "bg-red-500/10" };
          return (
            <div key={card.label} className="bg-[#11141D] border border-white/5 rounded-xl p-4 hover:border-white/10 transition-all" data-testid={`stat-card-${card.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] text-slate-500 uppercase tracking-wider font-medium">{card.label}</p>
                  <p className={cn("text-2xl font-bold mt-1", colorMap[card.color])} style={{ fontFamily: "Outfit, sans-serif" }}>{card.value}</p>
                </div>
                <div className={cn("p-2 rounded-lg", bgMap[card.color])}>
                  <card.icon className={cn("h-5 w-5", colorMap[card.color])} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Filter Buttons */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="quick-filters">
        {QUICK_FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setQuickFilter(f.key)}
            className={cn(
              "px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all",
              quickFilter === f.key ? f.color : "text-slate-500 bg-[#0B0D12] border-white/5 hover:bg-white/5 hover:text-slate-300"
            )}
            data-testid={`quick-filter-${f.key.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {f.label}
            <span className="ml-1.5 opacity-60">({statusCount(f.key)})</span>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-[#11141D] border border-white/5 rounded-xl overflow-hidden" data-testid="activity-table">
        {loading ? (
          <div className="flex items-center justify-center p-16">
            <RefreshCw className="h-6 w-6 animate-spin text-slate-500" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-500">
            <Activity className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm font-medium">No {quickFilter !== "all" ? quickFilter.toLowerCase() : ""} publishers found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-white/5 bg-[#0B0D12] hover:bg-[#0B0D12]">
                  <ColHead label="Publisher" field="publisher_name" sortBy={sortBy} onClick={handleSort}><SortIcon field="publisher_name" /></ColHead>
                  <ColHead label="Calls (30D)" field="calls_30d" sortBy={sortBy} onClick={handleSort} center><SortIcon field="calls_30d" /></ColHead>
                  <ColHead label="Conversions" field="conversions_30d" sortBy={sortBy} onClick={handleSort} center><SortIcon field="conversions_30d" /></ColHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Offers Assigned</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-medium text-center">Numbers</TableHead>
                  <ColHead label="Status" field="status" sortBy={sortBy} onClick={handleSort} center><SortIcon field="status" /></ColHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Reason</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Created Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.slice((currentPage - 1) * perPage, currentPage * perPage).map((pub, i) => {
                  const cfg = STATUS_CFG[pub.status] || STATUS_CFG.Inactive;
                  const rd = ringbaData[pub.publisher_name];
                  const campaigns = rd?.campaigns || [];
                  const numbers = rd?.numbers || [];
                  return (
                    <TableRow
                      key={pub.publisher_id || pub.publisher_name + i}
                      className={cn("border-b border-white/5 hover:bg-white/[0.02] transition-colors", cfg.rowBg)}
                      data-testid={`publisher-row-${(pub.publisher_name || "").replace(/\s+/g, "-")}`}
                    >
                      <TableCell className="py-3">
                        <p className="group font-medium text-slate-200 text-sm inline-flex items-center gap-1">
                          {pub.publisher_name}
                          <CopyButton text={pub.publisher_name} label="publisher name" testId={`copy-name-${(pub.publisher_name || "").replace(/\s+/g, "-")}`} />
                        </p>
                        {pub.publisher_id && (
                          <p className="group text-[10px] text-slate-600 mt-0.5 inline-flex items-center gap-1 font-mono">
                            <span className="truncate max-w-[220px]">{pub.publisher_id}</span>
                            <CopyButton text={pub.publisher_id} label="publisher ID" testId={`copy-id-${(pub.publisher_name || "").replace(/\s+/g, "-")}`} />
                          </p>
                        )}
                        {pub.publisher_email && <p className="text-[11px] text-slate-500 mt-0.5">{pub.publisher_email}</p>}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className={cn("text-sm font-semibold tabular-nums", (pub.calls_30d || 0) > 0 ? "text-slate-200" : "text-slate-600")} style={{ fontFamily: "Outfit, sans-serif" }}>
                          {(pub.calls_30d || 0).toLocaleString()}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className={cn("text-sm font-semibold tabular-nums", (pub.conversions_30d || 0) > 0 ? "text-emerald-400" : "text-slate-600")} style={{ fontFamily: "Outfit, sans-serif" }}>
                          {pub.conversions_30d || 0}
                        </span>
                      </TableCell>
                      <TableCell>
                        {campaigns.length === 0 ? (
                          <span className="text-xs text-slate-600">—</span>
                        ) : (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium bg-blue-500/10 text-blue-400 border border-blue-500/15 hover:bg-blue-500/20 transition-colors cursor-pointer" data-testid={`offers-btn-${(pub.publisher_name || "").replace(/\s+/g, "-")}`}>
                                <Briefcase className="h-3 w-3" />
                                {campaigns.length} {campaigns.length === 1 ? "Offer" : "Offers"}
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-72 p-0 bg-[#0c0f18] border-white/10" align="start">
                              <div className="px-3 py-2 border-b border-white/5">
                                <p className="text-xs font-semibold text-slate-300">{pub.publisher_name}</p>
                                <p className="text-[10px] text-slate-500">{campaigns.length} campaign{campaigns.length !== 1 ? "s" : ""} assigned</p>
                              </div>
                              <div className="max-h-48 overflow-y-auto py-1">
                                {campaigns.map((c, ci) => (
                                  <div key={ci} className="px-3 py-1.5 hover:bg-white/[0.03] flex items-start gap-2">
                                    <Briefcase className="h-3 w-3 text-blue-400/60 mt-0.5 shrink-0" />
                                    <span className="text-xs text-slate-300 leading-tight">{c}</span>
                                  </div>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {numbers.length === 0 ? (
                          <span className="text-xs text-slate-600">—</span>
                        ) : (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-800 text-slate-300 border border-white/5 hover:bg-slate-700 transition-colors cursor-pointer" data-testid={`numbers-btn-${(pub.publisher_name || "").replace(/\s+/g, "-")}`}>
                                <Phone className="h-3 w-3 text-slate-500" />
                                {numbers.length}
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-56 p-0 bg-[#0c0f18] border-white/10" align="center">
                              <div className="px-3 py-2 border-b border-white/5">
                                <p className="text-xs font-semibold text-slate-300">{pub.publisher_name}</p>
                                <p className="text-[10px] text-slate-500">{numbers.length} number{numbers.length !== 1 ? "s" : ""} assigned</p>
                              </div>
                              <div className="max-h-48 overflow-y-auto py-1">
                                {numbers.map((n, ni) => (
                                  <div key={ni} className="group px-3 py-1.5 hover:bg-white/[0.03] flex items-center gap-2">
                                    <Phone className="h-3 w-3 text-emerald-400/60 shrink-0" />
                                    <span className="text-xs text-slate-300 font-mono">{n}</span>
                                    <CopyButton text={n} label="phone number" testId={`copy-number-${n}`} />
                                  </div>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className={cn("inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border", cfg.bg, cfg.text, cfg.border)}>
                          <span className={cn("w-1.5 h-1.5 rounded-full", cfg.dot)} />
                          {pub.status}
                        </span>
                      </TableCell>
                      <TableCell><span className="text-xs text-slate-500">{activityReason(pub)}</span></TableCell>
                      <TableCell className="text-xs text-slate-500 whitespace-nowrap" title={pub.first_call_at ? `First call: ${formatDate(pub.first_call_at)}` : undefined}>{formatDate(pub.publisher_created_at)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      <PaginationControls currentPage={currentPage} totalPages={Math.max(1, Math.ceil(filtered.length / perPage))} totalItems={filtered.length} setCurrentPage={setCurrentPage} testIdPrefix="pub-activity-pagination" />
    </div>
  );
}

function ColHead({ label, field, sortBy, onClick, center, children }) {
  return (
    <TableHead
      className={cn("cursor-pointer hover:text-slate-200 text-[10px] uppercase tracking-wider text-slate-500 font-medium", center && "text-center")}
      onClick={() => onClick(field)}
    >
      <div className={cn("flex items-center", center && "justify-center")}>{label}{children}</div>
    </TableHead>
  );
}
